import { Theme, ThemeProvider, createTheme } from '@mui/material/styles';
import { ErrorBoundary } from '@sentry/react';
import { Suspense, lazy, useEffect, useMemo } from 'react';
import { Route, Router, Switch } from 'wouter';

import { ErrorFallback } from '~/modules/elements/error-fallback';
import LayoutWithBackgroundProvider from '~/modules/elements/layout-with-background';
import PageLoader from '~/modules/elements/page-loader';
import VolumeScroll from '~/modules/elements/volume-scroll';
import useMobileModeDisabled from '~/modules/hooks/use-mobile-mode-disabled';
import GetSongsBPMs from '~/routes/edit/get-songs-bp-ms';
import ExcludeLanguages from '~/routes/exclude-languages/exclude-languages';
import Game from '~/routes/game/game';
import { KeyboardHelpProvider } from '~/routes/keyboard-help/context';
import LandingPage from '~/routes/landing-page/landing-page';
import ManageSongs from '~/routes/manage-songs/manage-songs';
import QuickSetup from '~/routes/quick-setup/quick-setup';
import RemoteMic from '~/routes/remote-mic/remote-mic';
import routePaths from '~/routes/route-paths';
import { CalibrationSettings } from '~/routes/settings/calibration';
import RemoteMicSettings from '~/routes/settings/remote-mic-settings';
import Settings from '~/routes/settings/settings';
import { GraphicSetting, MobilePhoneModeSetting, useSettingValue } from '~/routes/settings/settings-state';
import SocialMediaElements from '~/routes/social-media-elements/social-media-elements';
import Welcome from '~/routes/welcome/welcome';

import Convert from './routes/convert/convert';
import Edit from './routes/edit/edit';
import Jukebox from './routes/jukebox/jukebox';
import SelectInput from './routes/select-input/select-input';

const LazySongList = lazy(() =>
  import('~/routes/manage-songs/song-management').then((modules) => ({ default: modules.SongList })),
);
const LazyAdmin = lazy(() => import('~/routes/admin/admin'));
const LazyOnline = lazy(() => import('~/routes/online/online'));
const LazySingers = lazy(() => import('~/routes/singers/singers-page'));
const LazySetlist = lazy(() => import('~/routes/edit/setlists').then((modules) => ({ default: modules.default })));
// import.meta.env.DEV is statically replaced at build time, so this whole branch (and the dev-screenshots
// module's eager glob import of the test screenshot PNGs) is dead-code-eliminated from production builds.
const LazyDevScreenshots = import.meta.env.DEV ? lazy(() => import('~/routes/dev-screenshots/dev-screenshots')) : null;

// Commenting this out as there are many failed to fetch errors coming from Googlebot
// // This is a hack to preload the game scene so that it's ready when the user clicks on the game button
// // without increasing initial load time. Vite doesn't support prefetch yet
// // https://github.com/vitejs/vite/issues/10600
// const prefetchGame = import('./routes/game/game');
// const LazyGame = lazy(() => prefetchGame);

function App() {
  const [graphicSetting] = useSettingValue(GraphicSetting);

  const mobileModeDisabled = useMobileModeDisabled();
  const [mobilePhoneMode, setMobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
  useEffect(() => {
    // Force it off for users who opted in before the flag loaded, or in a previous session,
    // while the disable-mobile-mode experiment is running.
    if (mobileModeDisabled && mobilePhoneMode) {
      setMobilePhoneMode(false);
    }
  }, [mobileModeDisabled, mobilePhoneMode, setMobilePhoneMode]);

  const theme = useMemo<Theme>(
    () =>
      createTheme({
        graphicSetting,
      }),
    [graphicSetting],
  );

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary fallback={ErrorFallback}>
        <LayoutWithBackgroundProvider>
          <KeyboardHelpProvider>
            <VolumeScroll />
            <Router base={import.meta.env.BASE_URL}>
              <Switch>
                <Route path={routePaths.QUICK_SETUP} component={QuickSetup} />
                <Route path={routePaths.MENU} component={Welcome} />
                <Route path={routePaths.EXCLUDE_LANGUAGES} component={ExcludeLanguages} />
                <Route path={routePaths.JUKEBOX} component={Jukebox} />
                {/* One page with three regions - see `singers-page`. Each keeps its own path so the
                    main menu's three entries survive, every existing link still resolves, and the
                    path picks the region the page opens on. */}
                <Route
                  path={routePaths.HISTORY}
                  component={() => (
                    <Suspense fallback={<PageLoader />}>
                      <LazySingers tab="history" />
                    </Suspense>
                  )}
                />
                <Route
                  path={routePaths.PROFILES}
                  component={() => (
                    <Suspense fallback={<PageLoader />}>
                      <LazySingers tab="profiles" />
                    </Suspense>
                  )}
                />
                <Route
                  path={routePaths.STATS}
                  component={() => (
                    <Suspense fallback={<PageLoader />}>
                      <LazySingers tab="stats" />
                    </Suspense>
                  )}
                />
                <Route path={routePaths.GAME}>
                  {/*<Suspense fallback={<PageLoader />}><LazyGame /></Suspense>*/}
                  <Game />
                </Route>
                {/* One entry for the room and the host's song browser (`online/pick-song`): a second
                    <Route> would remount `Online` on the way in and drop the room connection */}
                <Route path={`${routePaths.ONLINE}/:section?`}>
                  <Suspense fallback={<PageLoader />}>
                    <LazyOnline />
                  </Suspense>
                </Route>
                <Route path={routePaths.SELECT_INPUT} component={SelectInput} />
                <Route path={routePaths.SETTINGS} component={Settings} />
                <Route path={routePaths.SETTINGS_REMOTE_MICS} component={RemoteMicSettings} />
                <Route path={routePaths.SETTINGS_CALIBRATION} component={CalibrationSettings} />
                <Route path={routePaths.REMOTE_MIC} component={RemoteMic} />
                <Route path={routePaths.MANAGE_SONGS} component={ManageSongs} />
                <Route
                  path={routePaths.ADMIN}
                  component={() => (
                    <Suspense fallback={<PageLoader />}>
                      <LazyAdmin />
                    </Suspense>
                  )}
                />
                <Route path="social-media-elements" component={SocialMediaElements} />
                {LazyDevScreenshots && (
                  <Route
                    path="dev/screenshots"
                    component={() => (
                      <Suspense fallback={<PageLoader />}>
                        <LazyDevScreenshots />
                      </Suspense>
                    )}
                  />
                )}
                <Route path={routePaths.CONVERT} component={() => <Convert />} />
                <Route
                  path={routePaths.EDIT_SONGS_LIST}
                  component={() => (
                    <Suspense fallback={<PageLoader />}>
                      <LazySongList />
                    </Suspense>
                  )}
                />
                <Route path="edit/get-songs-bpms" component={GetSongsBPMs} />
                <Route path={routePaths.EDIT_SONG}>
                  <Edit />
                </Route>
                <Route
                  path={routePaths.EDIT_SETLISTS}
                  component={() => (
                    <Suspense fallback={<PageLoader />}>
                      <LazySetlist />
                    </Suspense>
                  )}
                />
                <Route path={routePaths.INDEX} component={LandingPage} />
              </Switch>
            </Router>
          </KeyboardHelpProvider>
        </LayoutWithBackgroundProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
